// import {initFirebaseAdminApp} from "seacrush-core/dist/service/external/firebase-admin-initializer"
import {FC, useRef} from "react";
import {useEffect} from "react";
import type {NextPage} from "next";
import type {AppProps} from "next/app";
import Head from "next/head";
import Router from "next-translate-routes/router";
import {Toaster} from "react-hot-toast";
import {Provider as ReduxProvider} from "react-redux";
import nProgress from "nprogress";
import {CacheProvider} from "@emotion/react";
import type {EmotionCache} from "@emotion/cache";
import {ThemeProvider} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {SettingsButton} from "../src/components/settings-button";
import {SplashScreen} from "../src/components/splash-screen";
import {SettingsConsumer, SettingsProvider} from "../src/contexts/settings-context";
import {AuthConsumer, AuthProvider} from "../src/contexts/firebase-auth-context";
import {gtmConfig} from "../src/config";
import {gtm} from "../src/lib/gtm";
import {createTheme} from "../src/theme";
import {createEmotionCache} from "../src/utils/create-emotion-cache";
import {withTranslateRoutes} from "next-translate-routes";
import {appWithTranslation, useTranslation} from "next-i18next"
import {i18nConfig} from "../src/i18nextConfig";
import {QueryParamProvider} from "use-query-params";
import {NextAdapter} from "next-query-params";
import {Onboarding} from "../src/components/onboarding/onboarding";
import {AuthWall} from "../src/components/auth-wall";
import {PersistGate} from "redux-persist/integration/react";
import {SEObyLang} from "../src/next-seo.config";
import {DefaultSeo} from "next-seo";
import {ForexProvider} from "../src/contexts/forex-context";
import {Analytics} from "@vercel/analytics/react";
import {SpeedInsights} from "@vercel/speed-insights/next";

// order is important b/c we're overridding some whatsapp chat widget stuff
import "react-whatsapp-chat-widget/index.css";
import "../src/global.scss"
import {AppStore, wrapper, CustomStore} from "../src/store";

//
// if (typeof window != "undefined") {
//     initFirebaseAdminApp()
// }

type EnhancedAppProps = AppProps & {
    Component: NextPage;
    emotionCache: EmotionCache;
}

Router.events.on("routeChangeStart", nProgress.start);
Router.events.on("routeChangeError", nProgress.done);
Router.events.on("routeChangeComplete", nProgress.done);

const clientSideEmotionCache = createEmotionCache();

const App: FC<EnhancedAppProps> = ({Component, ...rest}) => {
    const {store, props} = wrapper.useWrappedStore(rest)

    const {emotionCache = clientSideEmotionCache, pageProps} = props;

    const getLayout = Component.getLayout ?? ((page) => page);
    const {t, i18n} = useTranslation()

    const SEO = SEObyLang[i18n.language] ?? SEObyLang["en"]

    useEffect(() => {
        gtm.initialize(gtmConfig);
    }, []);

    useEffect(() => {

        if (i18n.language === "default") {

            for (const lang of navigator.languages) {
                if (lang.startsWith("en")) {
                    window.location.href = "/en"
                    break
                } else if (lang.startsWith("fr")) {
                    window.location.href = "/fr"
                    break
                } else if (lang.startsWith("es")) {
                    window.location.href = "/es"
                    break
                }
            }
            window.location.href = "/en"
        }

    }, [i18n.language])


    return (
        <>
            <CacheProvider value={emotionCache}>
                <Head>
                    <title>
                        SeaCrush
                    </title>
                    <meta
                        name='viewport'
                        content='initial-scale=1, width=device-width'
                    />
                    <link rel='icon'
                          type='image/svg+xml'
                          href={`/favicon/favicon_${process.env.NEXT_PUBLIC_ENV || "production"}_light.svg`}
                          media='(prefers-color-scheme:no-preference)'
                    />
                    <link rel='icon'
                          type='image/svg+xml'
                          href={`/favicon/favicon_${process.env.NEXT_PUBLIC_ENV || "production"}_dark.svg`}
                          media='(prefers-color-scheme:dark)'
                    />
                    <link rel='icon'
                          type='image/svg+xml'
                          href={`/favicon/favicon_${process.env.NEXT_PUBLIC_ENV || "production"}_light.svg`}
                          media='(prefers-color-scheme:light)'
                    />
                </Head>
                <DefaultSeo {...SEO} />
                <ReduxProvider store={store}>
                    <PersistGate persistor={(store as CustomStore).__persistor}>
                        {() => (
                            <QueryParamProvider adapter={NextAdapter}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <AuthProvider>
                                        <SettingsProvider>
                                            <SettingsConsumer>
                                                {({settings}) => {
                                                    let theme = createTheme({
                                                        direction: settings.direction,
                                                        responsiveFontSizes: settings.responsiveFontSizes,
                                                        mode: settings.theme
                                                    });
                                                    return (
                                                        <ThemeProvider
                                                            theme={theme}
                                                        >
                                                            <ForexProvider>
                                                                <CssBaseline/>
                                                                <Toaster
                                                                    toastOptions={{
                                                                        // className: '',
                                                                        position: "top-center",
                                                                        style: {
                                                                            border: "1px solid",
                                                                            borderColor: theme.palette.divider,
                                                                            borderRadius: 0,
                                                                            color: theme.palette.primary.main
                                                                        },
                                                                    }}
                                                                />
                                                                <SettingsButton/>
                                                                <AuthConsumer>
                                                                    {
                                                                        (auth) => {

                                                                        return (<>

                                                                                {getLayout(<Component {...pageProps} />)}

                                                                            <Analytics debug={false} mode={'production'}/>
                                                                            {!auth.isInitialized ? <SplashScreen/> : null}
                                                                            <AuthWall/>
                                                                            <Onboarding/>
                                                                            </>);
                                                                            }}
                                                                </AuthConsumer>
                                                            </ForexProvider>
                                                        </ThemeProvider>
                                                    );
                                                }}
                                            </SettingsConsumer>
                                        </SettingsProvider>
                                    </AuthProvider>
                                </LocalizationProvider>
                            </QueryParamProvider>
                        )}
                    </PersistGate>
                </ReduxProvider>
            </CacheProvider>
            <SpeedInsights debug={false}/>
        </>
    );
};

export default withTranslateRoutes(JSON.parse(`{"debug":false,"defaultLocale":"default","locales":["default","en","fr","es"],"routesTree":{"name":"","paths":{"default":""},"children":[{"name":"401","paths":{"default":"401"}},{"name":"_middleware","paths":{"default":"_middleware"}},{"name":"about","paths":{"default":"about","en":"about","fr":"a-propos","es":"acerca-de-nosotros"}},{"name":"concept","paths":{"default":"concept"}},{"name":"contact","paths":{"default":"contact"}},{"name":"index","paths":{"default":"index"}},{"name":"interested","paths":{"default":"interested","en":"interested","fr":"interesse","es":"interesado"}},{"name":"privacy","paths":{"default":"privacy","en":"privacy","fr":"politique-de-confidentialite","es":"politica-de-privacidad"}},{"name":"request-booking","paths":{"default":"request-booking"}},{"name":"settings","paths":{"default":"settings"}},{"name":"terms","paths":{"default":"terms","en":"terms","fr":"conditions-utilisation","es":"terminos-de-uso"}},{"name":"tradeshow","paths":{"default":"tradeshow"}},{"name":"admin","paths":{"default":"admin"},"children":[{"name":"country-gen","paths":{"default":"country-gen"}}]},{"name":"articles","paths":{"default":"articles","en":"blog","fr":"articles","es":"articulos"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[articleSlug]","paths":{"default":":articleSlug"}}]},{"name":"authentication","paths":{"default":"authentication"},"children":[{"name":"login","paths":{"default":"login"}},{"name":"password-recovery","paths":{"default":"password-recovery"}},{"name":"password-reset","paths":{"default":"password-reset"}},{"name":"register","paths":{"default":"register"}},{"name":"verify-code","paths":{"default":"verify-code"}}]},{"name":"deletion-status","paths":{"default":"deletion-status"},"children":[{"name":"[code]","paths":{"default":":code"}}]},{"name":"diving","paths":{"default":"diving","en":"diving","fr":"plongee","es":"buceo"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[countrySlug]","paths":{"default":":countrySlug"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[tripSlug]","paths":{"default":":tripSlug"}}]}]},{"name":"rate-me","paths":{"default":"rate-me"},"children":[{"name":"[username]","paths":{"default":":username"}}]},{"name":"t","paths":{"default":"t"},"children":[{"name":"[tripId]","paths":{"default":":tripId"}}]},{"name":"u","paths":{"default":"u"},"children":[{"name":"[username]","paths":{"default":":username"}}]}]},"fallbackLng":{}}`), appWithTranslation(App, i18nConfig))
